import { useEffect, useRef, useState } from "react";
import HeroSection from "./component/HeroSection";
import TeamSection from "./component/TeamSection";
import ReviewsSection from "./component/ReviewSection";
import BuySection from "./component/BuySection";
import AboutSection from "./component/AboutSection";
import ServicesSection from "./component/ServicesSection";
import StickyInfoBox from "./component/StickyInfoBox";

const SECTIONS = ["photos", "services", "team", "reviews", "buy", "about"];

export default function WillominaPage() {
  const [showSticky, setShowSticky] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const heroRef = useRef();

  // Show sticky menu after Hero
  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        setShowSticky(window.scrollY > heroRef.current.offsetHeight - 80);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll to section
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // Track section in view
  useEffect(() => {
    const sectionIds = [
      "photos",
      "services",
      "team",
      "reviews",
      "buy",
      "about",
    ];
    const observers = [];

    // Wait a bit to ensure all DOM elements are rendered
    const timeout = setTimeout(() => {
      sectionIds.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) {
          console.warn(`Section #${id} not found in DOM`);
          return;
        }

        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setActiveSection(id);
              console.log("🟢 Visible section:", id);
            }
          },
          {
            rootMargin: "-30% 0px -60% 0px", // top 30% of screen triggers
            threshold: 0.1,
          }
        );

        observer.observe(el);
        observers.push(observer);
      });
    }, 100); // delay allows layout to finish

    return () => {
      clearTimeout(timeout);
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  useEffect(() => {
    console.log("Active section changed:", activeSection);
  }, [activeSection]);

  const categoryButtonRefs = useRef({});

  useEffect(() => {
    const activeEl = categoryButtonRefs.current[activeSection];
    if (activeEl) {
      activeEl.scrollIntoView({
        behavior: "smooth",
        inline: "start",
        block: "nearest",
      });
    }
  }, [activeSection]);

  return (
    <div className="relative font-sans text-text">
      {/* Sticky Top Menu */}
      {showSticky && (
        <>
          {/* 🔻 Mobile Header + Scrollable Tabs */}
          <div className="fixed top-0 left-0 right-0 bg-dry z-50 border-b border-border lg:hidden">
            <div className="flex items-center justify-between px-4 py-2">
              <button className="text-xl">←</button>
              <h1 className="font-semibold text-white">Willomina</h1>
              <div className="flex items-center gap-4 text-xl">
                <button>🔗</button>
                <button>🤍</button>
              </div>
            </div>

            <div className="overflow-x-auto  ">
              <div className="flex gap-3 px-4 pb-2 text-sm font-medium min-w-max">
                {SECTIONS.map((s) => {
                  const id = s.toLowerCase();
                  const isActive = activeSection === id;

                  return (
                    <button
                      key={id}
                      ref={(el) => (categoryButtonRefs.current[id] = el)}
                      onClick={() => scrollToSection(id)}
                      className={`uppercase whitespace-nowrap px-4 py-2 rounded-full   transition-all duration-200 text-sm font-semibold ${
                        isActive
                          ? " text-redlight shadow underline underline-offset-[15px]"
                          : " text-textGray hover:bg-red-600 hover:text-white"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 🔺 Desktop Centered Menu */}
          <div className="fixed top-0 left-0 right-0 bg-border z-50 shadow hidden lg:flex justify-center px-6 py-4 gap-4">
            {SECTIONS.map((s) => {
              const id = s.toLowerCase();
              const isActive = activeSection === id;

              return (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className={`relative uppercase whitespace-nowrap px-4 py-1 text-sm font-semibold transition-all ${
                    isActive
                      ? "text-red-600 font-bold underline underline-offset-8"
                      : "text-textGray hover:text-white"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Hero Section */}
      <section
        id="photos"
        ref={heroRef}
        className="w-full container relative overflow-hidden mt-[30px] lg:mt-[100px]  mb-[100px]"
      >
        <HeroSection />
      </section>

      {/* Main Content + Sticky Info Box */}
      <div className="container relative flex gap-10">
        {/* Left Content Sections */}
        <div className="flex-1 flex flex-col sm:gap-0 gap-10">
          {["services", "team", "reviews", "buy", "about"].map((section) => (
            <section
              id={section}
              key={section}
              className={`min-h-[500px] border sm:border-0 border-border sm:rounded-0 py-20 px-6 ${
                section === "team" || section === "buy" ? "" : ""
              }`}
            >
              {section === "services" && <ServicesSection />}
              {section === "team" && <TeamSection />}
              {section === "reviews" && <ReviewsSection />}
              {section === "buy" && <BuySection />}
              {section === "about" && <AboutSection />}
            </section>
          ))}
        </div>

        {/* Right Sticky Info Box */}
        <div className="hidden lg:block w-[300px] shrink-0 sticky top-28 h-fit">
          <StickyInfoBox />
        </div>
      </div>
    </div>
  );
}
